/* Cozy Snugglet storefront: add-to-cart that opens Horizon's cart drawer, product page logic, countdowns. */
import { CartLinesUpdateEvent } from '@shopify/events';

const money = (cents) => '$' + (cents % 100 === 0 ? cents / 100 : (cents / 100).toFixed(2));

/**
 * Add lines to the cart, then let Horizon's cart drawer re-render and open itself.
 * @param {{id:number, quantity:number}[]} items
 */
export async function addToCart(items) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(window.Shopify?.routes?.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const body = await res.json();
    if (!res.ok || body.status) throw new Error(body.description || body.message || 'Could not add to cart');
    const cart = await fetch(window.Shopify?.routes?.root + 'cart.js').then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: items.reduce((n, i) => n + i.quantity, 0), source: 'cozy-shop', didError: false },
    });
    if (!document.querySelector('theme-drawer#cart-drawer')) window.location.href = '/cart';
    return cart;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}
window.csAddToCart = addToCart;

/* Countdown text: "16d 7h 28m" */
function initCountdowns(root = document) {
  root.querySelectorAll('[data-cs-countdown]').forEach((el) => {
    if (el.dataset.csInit) return;
    el.dataset.csInit = '1';
    const end = new Date(el.dataset.end.replace(/([+-]\d\d)(\d\d)$/, '$1:$2')).getTime();
    const out = el.querySelector('[data-cs-left]');
    const tick = () => {
      const ms = end - Date.now();
      if (isNaN(end) || ms <= 0) return el.remove();
      const s = Math.floor(ms / 1000);
      const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
      out.textContent = d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${sec}s`;
      setTimeout(tick, d > 0 ? 30000 : 1000);
    };
    tick();
  });
}

class CsProduct {
  constructor(root) {
    this.root = root;
    this.data = JSON.parse(root.querySelector('[data-cs-product-json]').textContent);
    this.form = root.querySelector('[data-cs-form]');
    this.idInput = root.querySelector('[data-cs-variant-id]');
    this.addBtn = root.querySelector('[data-cs-add]');
    this.addLabel = root.querySelector('[data-cs-add-label]');
    this.err = root.querySelector('[data-cs-error]');
    this.qty = root.querySelector('[data-cs-qty-input]');
    this.track = root.querySelector('[data-cs-track]');
    this.thumbs = [...root.querySelectorAll('[data-cs-thumb]')];
    this.dots = [...root.querySelectorAll('.cs2-gallery__dots i')];
    this.fieldsets = [...root.querySelectorAll('[data-cs-option]')];
    this.variant = this.data.variants.find((v) => String(v.id) === this.idInput.value) || this.data.variants[0];

    this.fieldsets.forEach((fs) => fs.addEventListener('change', () => this.onOptionChange()));
    root.querySelectorAll('[data-cs-qty]').forEach((b) =>
      b.addEventListener('click', () => {
        this.qty.value = Math.min(20, Math.max(1, (parseInt(this.qty.value, 10) || 1) + Number(b.dataset.csQty)));
      })
    );
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.add();
    });
    this.initGallery();
    this.initSticky();
    this.updateAvailability();
  }

  selectedOptions() {
    return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
  }

  onOptionChange() {
    const sel = this.selectedOptions();
    let v = this.data.variants.find((x) => x.options.every((o, i) => o === sel[i]));
    if (!v) {
      // Keep the option just picked, fall back to the first available variant that has it.
      v = this.data.variants.find((x) => x.available && x.options[0] === sel[0]) || this.data.variants.find((x) => x.options[0] === sel[0]);
      if (v) v.options.forEach((o, i) => { const inp = [...this.fieldsets[i].querySelectorAll('input')].find((n) => n.value === o); if (inp) inp.checked = true; });
    }
    if (!v) return;
    this.variant = v;
    this.idInput.value = v.id;
    this.fieldsets.forEach((fs, i) => { const out = fs.querySelector('[data-cs-option-value]'); if (out) out.textContent = v.options[i]; });
    this.renderPrice();
    this.updateAvailability();
    const url = new URL(window.location.href);
    url.searchParams.set('variant', v.id);
    history.replaceState(null, '', url);
    this.showLead(v);
  }

  renderPrice() {
    const v = this.variant;
    const el = this.root.querySelector('[data-cs-price]');
    let html = `<b>${money(v.price)}</b>`;
    if (v.compare > v.price) html += `<s>${money(v.compare)}</s><em>Save ${money(v.compare - v.price)}</em>`;
    el.innerHTML = html;
    const sp = this.root.querySelector('[data-cs-sticky-price]');
    if (sp) sp.textContent = money(v.price);
  }

  updateAvailability() {
    const sel = this.selectedOptions();
    // Disable values that have no available variant given the other selections.
    this.fieldsets.forEach((fs, i) => {
      fs.querySelectorAll('input').forEach((inp) => {
        // Hierarchical: a value is pickable if some available variant has it plus the options chosen above it.
        const ok = this.data.variants.some((x) => x.available && x.options[i] === inp.value && x.options.every((o, j) => j >= i || o === sel[j]));
        inp.closest('label').classList.toggle('is-off', !ok);
      });
    });
    const avail = this.variant.available;
    this.addBtn.disabled = !avail;
    this.addLabel.textContent = avail ? 'Add to cart' : 'Sold out';
  }

  async add(btn = this.addBtn) {
    if (!this.variant.available) return;
    this.err.hidden = true;
    btn.disabled = true;
    const label = btn.querySelector('[data-cs-add-label]') || btn;
    const prev = label.textContent;
    label.textContent = 'Adding…';
    try {
      const items = [{ id: this.variant.id, quantity: parseInt(this.qty.value, 10) || 1 }];
      const addon = this.root.querySelector('[data-cs-addon-check]');
      const addonId = this.root.querySelector('[data-cs-addon-variant]')?.value;
      if (addon?.checked && addonId) items.push({ id: Number(addonId), quantity: 1 });
      await addToCart(items);
      label.textContent = 'Added ✓';
      setTimeout(() => { label.textContent = prev; btn.disabled = false; }, 1400);
    } catch (e) {
      label.textContent = prev;
      btn.disabled = false;
      this.err.textContent = e.message || 'Something went wrong. Please try again.';
      this.err.hidden = false;
    }
  }

  initGallery() {
    if (!this.track) return;
    const slides = [...this.track.children];
    this.slides = slides;
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.csThumb))));
    let raf;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.setActive(i);
      });
    }, { passive: true });
  }

  setActive(i) {
    this.thumbs.forEach((t, j) => t.classList.toggle('is-active', j === i));
    this.dots.forEach((d, j) => d.classList.toggle('is-active', j === i));
    const count = this.root.querySelector('[data-cs-count]');
    if (count) count.textContent = String(i + 1);
  }

  goTo(i) {
    if (!this.track) return;
    this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' });
    this.setActive(i);
  }

  /* Swap slide 1 to the chosen variant's photo and bring it into view. */
  showLead(v) {
    if (!v.img) return;
    const img = this.root.querySelector('[data-cs-lead] img');
    if (img && img.getAttribute('src') !== v.img) {
      img.removeAttribute('srcset');
      img.src = v.img;
    }
    const th = this.root.querySelector('[data-cs-lead-thumb] img');
    if (th && v.thumb) { th.removeAttribute('srcset'); th.src = v.thumb; }
    this.goTo(0);
  }

  initSticky() {
    const bar = this.root.querySelector('[data-cs-sticky]');
    const cta = this.root.querySelector('[data-cs-main-cta]');
    if (!bar || !cta || !('IntersectionObserver' in window)) return;
    const btn = bar.querySelector('[data-cs-sticky-add]');
    btn.addEventListener('click', () => this.add(btn));
    new IntersectionObserver(([e]) => {
      const show = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle('is-on', show);
      bar.setAttribute('aria-hidden', String(!show));
      btn.tabIndex = show ? 0 : -1;
    }).observe(cta);
  }
}

/* One-tap adds inside the cart (free-shipping gap filler, free third item). Falls back to the form post. */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-cs-quick-add]');
  if (!form) return;
  e.preventDefault();
  const btn = form.querySelector('button');
  if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
  try {
    await addToCart([{ id: Number(form.querySelector('[name="id"]').value), quantity: 1 }]);
    if (!document.querySelector('theme-drawer#cart-drawer') || location.pathname.endsWith('/cart')) location.reload();
  } catch (err) {
    form.submit();
  }
});

function init() {
  initCountdowns();
  document.querySelectorAll('[data-cs-pdp]').forEach((el) => {
    if (!el.csProduct) el.csProduct = new CsProduct(el);
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
document.addEventListener('shopify:section:load', init);
